/**
 * Everything the campaign list shows: every campaign with its status, the tab
 * counts, and the rows the current tab and search leave, plus the address-bar
 * tab and search that drive them.
 */
import { useEffect, useMemo, useState } from 'react';

import { useListFilterParams } from '../../hooks/use-list-filter-params';

import {
  ALL_CAMPAIGNS,
  CAMPAIGN_TABS,
  countCampaignsByTab,
  toCampaignListRows,
  visibleCampaignRows,
} from './ticket-sales-campaigns-list-model';
import { useAllTicketSalesCampaigns } from './use-all-ticket-sales-campaigns';

/** How often statuses are re-judged, so a campaign that ends while the page is open moves on. */
export const STATUS_REFRESH_MS = 60_000;

function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), STATUS_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);
  return now;
}

export function useTicketSalesCampaignsListView() {
  const params = useListFilterParams(CAMPAIGN_TABS, ALL_CAMPAIGNS);
  const query = useAllTicketSalesCampaigns();
  const now = useNow();
  const { tab, search } = params;

  const allRows = useMemo(() => toCampaignListRows(query.data ?? [], now), [query.data, now]);
  const counts = useMemo(() => countCampaignsByTab(allRows), [allRows]);
  const rows = useMemo(() => visibleCampaignRows(allRows, tab, search), [allRows, tab, search]);
  const loaded = query.data !== undefined;

  return {
    ...params,
    now,
    counts,
    rows,
    loaded,
    // A failed background refresh keeps the list it already has; a retry shows progress.
    loading: !loaded && (query.isPending || query.isFetching),
    error: loaded || query.isFetching ? null : query.error,
    retry: () => void query.refetch(),
  };
}
